import {
  Button,
  Input,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiLock } from 'react-icons/pi';
import { calculateShapeArea, CircleGeometry, Geometry } from '@ais-ui/utils';

interface ShapeAreaMetadataRowProps {
  geometry: Geometry | undefined;
  radius?: number;
}

/**
 * Fixed, read-only Metadata row showing the shape's live-calculated area.
 * Styled to match the free-form key-value rows below it, but locked (no key/
 * value editing, no remove) since 'area' is a reserved property maintained by
 * the system rather than user-entered Metadata (CLASSIC-10356).
 */
export const ShapeAreaMetadataRow = ({
  geometry,
  radius,
}: ShapeAreaMetadataRowProps) => {
  const areaResult = calculateShapeArea(geometry, radius);

  if (!areaResult) {
    return null;
  }

  return (
    <div className="flex space-x-2 items-center">
      <Button
        type="button"
        size="icon"
        disabled={true}
        variant="ghost"
        className="h-9 w-9"
        aria-label="Locked row"
      >
        <PiLock className="size-4" aria-hidden="true" />
      </Button>
      <Input
        type="text"
        value="area"
        readOnly
        disabled={true}
        aria-label="Metadata key"
        className="w-64"
      />
      <Input
        type="text"
        value={areaResult.displayFormatted}
        readOnly
        disabled={true}
        aria-label="Area"
        className="w-64"
      />
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            aria-label="Area is automatically calculated and can't be edited or removed"
          >
            <Button
              type="button"
              size="icon"
              variant="outline"
              className="h-9 w-9"
              disabled={true}
              aria-label="Area is automatically calculated and can't be edited or removed"
            >
              <PiLock className="size-4" aria-hidden="true" />
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          Automatically calculated from the shape&apos;s geometry — can&apos;t
          be edited or removed.
        </TooltipContent>
      </Tooltip>
    </div>
  );
};

/** Extracts the radius from a Point geometry drawn as a circle, if present. */
export const getCircleRadius = (
  geometry: Geometry | undefined,
): number | undefined =>
  geometry?.type === 'Point' && 'radius' in geometry
    ? (geometry as CircleGeometry).radius
    : undefined;
